/**
 * @namespace Utils
 * @category Utils
 * @module Promise*/

import { uuidv4 } from './crypto'
import sequential from 'promise-sequential'

/**
 * Used by Location module for a more precise loading state.
 * By calling hasPendingPromises the component that renders a loader can ensure there are no any search in progress.
 * @returns
 */
export function createPromiseTracker() {
  // Use Map instead of array for thread safety
  const tracker = new Map()
  tracker.trackPromise = function (promise) {
    if (!promise || typeof promise.then !== 'function') {
      throw new Error('Input must be a promise')
    }

    // Use promise as key in Map
    const timeoutId = setTimeout(() => {
      tracker.delete(promise)
    }, 60000)

    tracker.set(promise, timeoutId)
    let deleted = false
    promise
      .then(
        (result) => {
          // Clear timeout on resolve
          clearTimeout(timeoutId)

          // Remove from map on resolve
          tracker.delete(promise)
          return result
        },
        (error) => {
          // Handle rejections
          clearTimeout(timeoutId)
          tracker.delete(promise)
          deleted = true
        }
      )
      .catch((error) => {
        // Global rejection handler
        clearTimeout(timeoutId)
        if (!deleted) {
          tracker.delete(promise)
        }
      })
    return promise
  }

  tracker.hasPendingPromises = function () {
    return tracker.size > 0
  }

  return tracker
}

/**
 * Removes the first fulfilled promise from the array of promises.
 * If all promises are rejected, it will skip removing.
 *
 * Used for optimizations while splitting requests in parallel.
 *
 * @param {Promise[]} promises - The array of promises
 * @returns {Promise[]} - The promises array with first fulfilled promise removed
 */
export async function removeFirstResolved(promises, timeout = 10000) {
  //We try to remove any exist promise already resolved (sync)
  for (let i = 0; i < promises.length; i++) {
    const promise = promises[i]
    if (promise.status === 'fulfilled') {
      //If first resolved found, remove it and return copy
      return [...promises.slice(0, i), ...promises.slice(i + 1)]
    }
  }

  //We remove first resolved in the near future (async)

  try {
    //Store current indexes inside promises
    for (let currPromiseIndex in promises) {
      promises[currPromiseIndex]._index = currPromiseIndex
    }

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('TIMEOUT')), timeout)
    })
    const firstResolvedPromiseIndex = await Promise.race([
      Promise.any(promises.map((p) => p.then((v) => parseInt(p._index)))),
      timeoutPromise,
    ])
    const frpIndex = firstResolvedPromiseIndex
    //Race and remove first resolved, return copy
    return [...promises.slice(0, frpIndex), ...promises.slice(frpIndex + 1)]
  } catch (err) {
    console.warn({
      err,
      errCode: err.code,
      errMessage: err.message,
    })
    if (
      err.message === 'TIMEOUT' ||
      err.message.includes('All promises were rejected')
    ) {
      //Timeout wins, return original array
      promises[0]._timeout = err.message === 'TIMEOUT'
      return promises
    } else {
      //Other error, throw
      throw err
    }
  }
}

/**
 * Generic helper for progresive searchs.
 *
 * @deprecated Use nested "for of" loops intead. See messages module as example.
 *
 * Used to split heavy requests without pagination support (Geored APIV2).
 *
 * @param {Object} options
 * @param {function} options.generateSubsets Handler to generate subsets
 * @param {Function} options.handleSubset Handler to process and resolve subsets
 * @param {Function} options.withSubsetResult Handler to control a subset result
 * @param {Boolean} options.sequential Subsets will resolve sequentially
 */
export async function splitOperation(options = {}) {
  let subsets = options.generateSubsets()
  if (options.sequential === true) {
    return await sequential(
      subsets.map((subset, index) => {
        return async () => {
          let r = await options.handleSubset(subset, index, subsets.length)
          await withSubsetResult(r, subset)
          return r
        }
      })
    )
  } else {
    return await Promise.all(
      subsets.map((subset, index) =>
        (async () => {
          let r = await options.handleSubset(subset, index, subsets.length)
          await withSubsetResult(r, subset)
          return r
        })()
      )
    )
  }

  async function withSubsetResult(r, subset) {
    if (options.withSubsetResult) {
      let p = options.withSubsetResult(r, subset)
      if (p instanceof Promise) {
        await p
      }
    }
  }
}

/**
 * @warn This function is to complex
 * @deprecated Use a simple thottle function or something else
 * @todo Refactor/Simplify
 *
 * Queue function to be called after certain time (like setTimeout) but skips if already queued.
 *
 * Useful for delaying operations in vue watchers/computed properties (To avoid multiple simultaneous calls and performance issues)
 *
 * This code is an async function called queueOperationOnce with 3 parameters (uniqueId, handler and millisecondsOrOptions). The function checkes if the parameters are valid and then starts a timeout that calls the specified handler. This code is used to ensure that only one instance of an operation runs at any given time - when another instance of the same operation is triggered, the new instance will be skipped and cleared or replaced, depending on the specified option.
 *
 * @example
 *
 * queueOperationOnce('sayHello',()=>console.log('Hello'))
 * queueOperationOnce('sayHello',()=>console.log('Hello'))
 * Hello
 * Hello
 * (Operations will run sync at output right away)
 *
 * queueOperationOnce('queueHello', ()=>console.log('Hello'), 1000)
 * queueOperationOnce('queueHello', ()=>console.log('Hello'), 1000)
 * Hello
 * (Second fn will be ignored because already queued)
 * (Will be only called once within 1000ms) (Great for saving cpu when triggered from vue watchers)
 *
 * queueOperationOnce('queueHello', ()=>console.log('Hello'), {timeout:1000,clearPreviousTimeout:true })
 * queueOperationOnce('queueHello', ()=>console.log('Hello'),{timeout:1000,clearPreviousTimeout:true })
 * Hello
 * (Second fn will called) (First fn will be cleared/skipped)
 *
 * queueOperationOnce('queueHello', ()=>{async op that takes 4 seconds}, {timeout:200,isSequential:true })
 * //after 1 second
 * queueOperationOnce('queueHello', ()=>{async op that takes 4 seconds}, {timeout:200,isSequential:true })
 * (First fn will be called after 200ms) (Second fn will be skipped because already processing)
 *
 * queueOperationOnce('queueHello', ()=>{async op that takes 4 seconds}, {timeout:200,isSequential:true,sequentialQueues:true })
 * //after 1 second
 * queueOperationOnce('queueHello', ()=>{async op that takes 4 seconds}, {timeout:200,isSequential:true })
 * (First fn will be called after 200ms) (Second fn will be queued to be called after first)
 *
 * @param {String} uniqueId The unique identifier for the queue
 * @param {Function} handler handler to be queued and executed after certain time.
 * @param {Number} millisecondsOrOptions Timeout in milliseconds
 * @param {Number} millisecondsOrOptions.timeout Timeout in milliseconds (Not compatible with sequentialQueues)
 * @param {Boolean} millisecondsOrOptions.isSequential Mark as sequential action (Default behaviour: Skip if processing)
 * @param {Boolean} millisecondsOrOptions.sequentialQueues Feature to queue operation if already processing or queued
 * @param {Boolean} millisecondsOrOptions.clearPreviousTimeout If true, previous queued operations will be overrided
 */
export async function queueOperationOnce(
  uniqueId,
  handler,
  millisecondsOrOptions = 0
) {
  if (!uniqueId) {
    throw new Error('string expected (uniqueId)')
  }
  if (typeof handler !== 'function') {
    throw new Error('function expected (handler)')
  }
  let options =
    typeof millisecondsOrOptions !== 'object' ? {} : millisecondsOrOptions
  let timeoutMilliseconds =
    typeof millisecondsOrOptions !== 'object'
      ? millisecondsOrOptions
      : millisecondsOrOptions.timeout || 0

  const globalStateWinPropName = '_queueOperations'

  if (window[globalStateWinPropName] === undefined) {
    window[globalStateWinPropName] = {}
  }
  const hasActiveTimeout = () =>
    window[globalStateWinPropName][uniqueId] &&
    window[globalStateWinPropName][uniqueId].windowTimeout
  const removeActiveTimeout = () => {
    if (hasActiveTimeout()) {
      window.clearTimeout(
        window[globalStateWinPropName][uniqueId].windowTimeout
      )
      window[globalStateWinPropName][uniqueId].windowTimeout = null
    }
  }
  const createQueueScope = (obj) =>
    (window[globalStateWinPropName][uniqueId] = obj)

  const hasQueueScope = () => !!window[globalStateWinPropName][uniqueId]

  const updateQueueScope = (attributeName, value) => {
    if (hasQueueScope()) {
      window[globalStateWinPropName][uniqueId][attributeName] = value
    }
  }
  const getQueueScopeValue = (attributeName, defaultValue = null) =>
    window[globalStateWinPropName][uniqueId][attributeName] || defaultValue

  const isProcessing = () =>
    hasQueueScope() && window[globalStateWinPropName][uniqueId]['processing']

  const incrementQueueScopeValue = (attributeName) =>
    window[globalStateWinPropName][uniqueId][attributeName]++

  const internalId = uniqueId + uuidv4()

  return new Promise((resolve, reject) => {
    if (!options.sequentialQueues) {
      if (hasActiveTimeout()) {
        //Clear active timeout feat (Will ignored previous queued operation)
        if (options.clearPreviousTimeout) {
          removeActiveTimeout()
          incrementQueueScopeValue('clearCount')
        } else {
          //Skip if active timeout feat
          incrementQueueScopeValue('skipCount')
          return resolve() //Skip if the same operation is already queued
        }
      }

      //If sequential, avoid parallel
      if (options.isSequential && isProcessing()) {
        incrementQueueScopeValue('skipAlreadyProcessingCount')
        return resolve()
      }
    }

    const timeoutHandler = () => {
      let shouldQueue = false
      if (
        options.isSequential &&
        options.sequentialQueues &&
        (isProcessing() || hasActiveTimeout())
      ) {
        /*console.log('queueOperationOnce shouldQueue', {
          isProcessing: isProcessing(),
          hasActiveTimeout: hasActiveTimeout(),
          isSequential: options.isSequential,
          sequentialQueues: options.sequentialQueues,
        })*/
        shouldQueue = true
      }
      if (shouldQueue) {
        //Queue feat (isSequential + sequentialQueues)
        let queueArr = getQueueScopeValue('sequentialQueueArray', [])
        queueArr.push(run)
        incrementQueueScopeValue('queueingSequentialCount')
      } else {
        run()
        if (options.isSequential) {
          incrementQueueScopeValue('sequentialNotProcessingNormalRun')
        }
      }

      async function run() {
        try {
          updateQueueScope('processing', true)
          let handlerResponse = handler()
          if (handlerResponse instanceof Promise) {
            const timeoutPromise = new Promise((resolve, _reject) => {
              setTimeout(() => {
                resolve('TIMEOUT')
              }, options.operationHandlerTimeout || 30000)
            })

            const operationPromise = handlerResponse

            handlerResponse = await Promise.race([
              timeoutPromise,
              operationPromise,
            ])

            if (handlerResponse === 'TIMEOUT') {
              console.warn('queueOperationOnce ', {
                details: 'Operation handler timeout',
                options,
                internalId,
              })
              handlerResponse = null
            }

            incrementQueueScopeValue('resolvedCount')
            removeActiveTimeout()
            updateQueueScope('processing', false)
            resolveHandler(handlerResponse)
          } else {
            removeActiveTimeout()
            incrementQueueScopeValue('resolvedCount')
            updateQueueScope('processing', false)
            resolveHandler(handlerResponse)
          }
        } catch (err) {
          updateQueueScope('processing', false)
          removeActiveTimeout()
          reject(err)
        }
      }
    }

    //Lazy init for op state
    if (!hasQueueScope()) {
      createQueueScope({
        uniqueId,
        skipCount: 0,
        skipAlreadyProcessingCount: 0,
        clearCount: 0,
        resolvedCount: 0,
        windowTimeout: null,
        processing: false,
        queueingSequentialCount: 0,
        sequentialQueueArray: [],
        sequentialNotProcessingNormalRun: 0,
      })
    }

    //Timeout (yes or no)
    if (!timeoutMilliseconds) {
      timeoutHandler()
    } else {
      updateQueueScope(
        'windowTimeout',
        window.setTimeout(timeoutHandler, timeoutMilliseconds)
      )
    }

    //Helpers

    /**
     * Resolves operation and triggers next in the queue (if any)
     * @param {*} handlerResponse
     */
    function resolveHandler(handlerResponse) {
      resolve(handlerResponse)
      options.resolve && options.resolve(handlerResponse)

      //Process sequential queue (if any)
      if (options.isSequential) {
        let queueArr = getQueueScopeValue('sequentialQueueArray', [])
        if (queueArr.length > 0) {
          let firstHandler = queueArr.shift()
          updateQueueScope('sequentialQueueArray', queueArr)
          incrementQueueScopeValue('queueSequentialCallCount')
          firstHandler()
        }
      }
    }
  })
}

export function awaitSeconds(seconds = 5) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve()
    }, seconds * 1000)
  })
}
